/**
 * Investor service - API calls for the Investor Intelligence module
 */
import api from './api';
import { ApiResponse } from '../types';
import {
  Investor,
  InvestorListFilters,
  InvestorListResponse,
} from '../types/investor';

export async function fetchInvestors(
  filters?: InvestorListFilters
): Promise<ApiResponse<InvestorListResponse>> {
  try {
    const response = await api.get('/investors', { params: filters });
    const payload = response.data;

    // Tolerate either {investors: [...], total, page, size, pages} or a bare array
    if (Array.isArray(payload)) {
      return {
        data: {
          investors: payload,
          total: payload.length,
          page: 1,
          size: payload.length,
          pages: 1,
        },
      };
    }

    return {
      data: {
        investors: payload?.investors || payload?.items || [],
        total: payload?.total ?? 0,
        page: payload?.page ?? 1,
        size: payload?.size ?? 20,
        pages: payload?.pages ?? 1,
      },
    };
  } catch (error: any) {
    return { error: error.response?.data?.detail || 'Failed to load investors' };
  }
}

export async function fetchInvestor(id: number): Promise<ApiResponse<Investor>> {
  try {
    const response = await api.get(`/investors/${id}`);
    return { data: response.data };
  } catch (error: any) {
    return { error: error.response?.data?.detail || 'Failed to load investor' };
  }
}

export async function createInvestor(
  data: Partial<Investor>
): Promise<ApiResponse<Investor>> {
  try {
    const response = await api.post('/investors', data);
    return { data: response.data };
  } catch (error: any) {
    return { error: error.response?.data?.detail || 'Failed to create investor' };
  }
}

export async function updateInvestor(
  id: number,
  data: Partial<Investor>
): Promise<ApiResponse<Investor>> {
  try {
    const response = await api.patch(`/investors/${id}`, data);
    return { data: response.data };
  } catch (error: any) {
    return { error: error.response?.data?.detail || 'Failed to update investor' };
  }
}

export async function deleteInvestor(
  id: number
): Promise<ApiResponse<{ message: string }>> {
  try {
    const response = await api.delete(`/investors/${id}`);
    return { data: response.data };
  } catch (error: any) {
    return { error: error.response?.data?.detail || 'Failed to delete investor' };
  }
}

// ---------------------------------------------------------------------------
// Auto-Research Pipeline (Phase 2)
// ---------------------------------------------------------------------------

export interface ResearchTriggerResponse {
  investor_id: number;
  queued: boolean;
  research_status?: string | null;
  queue_size?: number;
  priority?: number;
}

export interface ResearchStatusResponse {
  investor_id: number;
  research_status?: string | null;
  last_researched_at?: string | null;
  dossier_generated_at?: string | null;
  has_dossier?: boolean;
  research_sources?: Array<Record<string, any>>;
}

export interface DossierResponse {
  investor_id: number;
  full_name: string;
  dossier?: string | null;
  dossier_generated_at?: string | null;
  research_status?: string | null;
  sources?: Array<Record<string, any>>;
}

export interface MediaDiscoveryResponse {
  investor_id: number;
  ok: boolean;
  reason: string | null;
  created: number;
  skipped: number;
  searches_used?: number;
  media: { id: number; media_type: string | null; title: string | null; url: string }[];
}

/** Find media appearances for one investor. Costs one SerpAPI search —
 *  deliberately operator-triggered, never run in bulk. */
export async function discoverMedia(
  id: number
): Promise<ApiResponse<MediaDiscoveryResponse>> {
  try {
    const response = await api.post(`/investors/${id}/media/discover`);
    return { data: response.data };
  } catch (error: any) {
    return {
      error: error.response?.data?.detail || 'Failed to search for media',
    };
  }
}

export async function triggerResearch(
  id: number,
  priority?: number
): Promise<ApiResponse<ResearchTriggerResponse>> {
  try {
    const response = await api.post(
      `/investors/${id}/research`,
      undefined,
      { params: priority !== undefined ? { priority } : undefined }
    );
    return { data: response.data };
  } catch (error: any) {
    return {
      error: error.response?.data?.detail || 'Failed to queue research',
    };
  }
}

export async function getResearchStatus(
  id: number
): Promise<ApiResponse<ResearchStatusResponse>> {
  try {
    const response = await api.get(`/investors/${id}/research/status`);
    return { data: response.data };
  } catch (error: any) {
    return {
      error: error.response?.data?.detail || 'Failed to load research status',
    };
  }
}

export async function getDossier(
  id: number
): Promise<ApiResponse<DossierResponse>> {
  try {
    const response = await api.get(`/investors/${id}/dossier`);
    return { data: response.data };
  } catch (error: any) {
    return {
      error: error.response?.data?.detail || 'Failed to load dossier',
    };
  }
}

const investorService = {
  fetchInvestors,
  fetchInvestor,
  createInvestor,
  updateInvestor,
  deleteInvestor,
  triggerResearch,
  getResearchStatus,
  getDossier,
  discoverMedia,
};

export default investorService;
